// MODULES
import { useCallback } from 'react';
import {
    NftAPI,
    ContactMessageI,
    ILotCreateRequest,
    LotI
} from '@tacans/shared-marketplace-layer';

// RESOURCES
import { useAppDispatch, useAppSelector } from 'src/hooks/app';
import { actions as CollectibleActions } from '../ducks/collectible/collectible.slice';
import {
    nftFetchThunk,
    fetchAllLotsThunk,
    setSelectedLotThunk,
    currentBidsFetchThunk,
    auctionWinnerFetchThunk,
    contactSellerThunk,
    collectibleCreateLotThunk,
    fetchActiveLotsThunk
} from '../ducks/collectible/collectible.thunk';
import {
    selectCollectible,
    selectCollectibleCreated,
    selectCollectibleError,
    selectCollectiblePublished,
    selectAuctionWinner,
    selectAllLots,
    selectCurrentBids,
    selectSelectedLot,
    selectActiveLots,
    selectNftCollection,
    selectSelectedLaunchpadId,
} from '../ducks/collectible/collectible.selectors';

export const useCollectibleDelete = () => useCallback(async (id: string) => {
    const response = await NftAPI.deleteNft(id);

    if (response?.data?.responseMeta?.status !== 'OK') {
        throw new Error('Error deleting collectible');
    }
}, []);

export const useNFTFetch = () => {
    const dispatch = useAppDispatch();

    return useCallback(
        async (id: string) => {
            await dispatch(nftFetchThunk(id));
        },
        [dispatch],
    );
};

export const useFetchAllLots = () => {
    const dispatch = useAppDispatch();

    return useCallback(
        async (id: string) => {
            await dispatch(fetchAllLotsThunk(id));
        },
        [dispatch],
    );
};

export const useFetchActiveLots = () => {
    const dispatch = useAppDispatch();

    return useCallback(
        async (id: string) => {
            await dispatch(fetchActiveLotsThunk(id));
        },
        [dispatch],
    );
};

export const useCurrentBidsFetch = () => {
    const dispatch = useAppDispatch();

    return useCallback(
        (lotIds: string[]) => {
            dispatch(currentBidsFetchThunk(lotIds));
        },
        [dispatch],
    );
};

export const useAuctionWinnerFetch = () => {
    const dispatch = useAppDispatch();

    return useCallback(
        (lotId: string) => {
            // @ts-ignore
            dispatch(auctionWinnerFetchThunk(lotId));
        },
        [dispatch],
    );
};

export const useCollectableLotCreate = () => {
    const dispatch = useAppDispatch();

    return useCallback(
        async (data: ILotCreateRequest) => {
            const res = await dispatch(collectibleCreateLotThunk(data));

            return res;
        },
        [dispatch],
    );
};

export const useCollectableClear = () => {
    const dispatch = useAppDispatch();

    return useCallback(() => {
        // @ts-ignore
        dispatch(CollectibleActions.clearCollectible());
    }, [dispatch]);
};

export const useCollectableErrorClear = () => {
    const dispatch = useAppDispatch();

    return useCallback(() => {
        // @ts-ignore
        dispatch(CollectibleActions.errorClear());
    }, [dispatch]);
};

export const useContactSeller = () => {
    const dispatch = useAppDispatch();

    return useCallback(
        async (data: ContactMessageI) => {
            const res = await dispatch(contactSellerThunk(data));

            return res;
        },
        [dispatch],
    );
};

export const useClearCollectibleError = () => {
    const dispatch = useAppDispatch();

    return useCallback(() => {
        dispatch(CollectibleActions.errorClear());
    }, [dispatch]);
};

export const useCollectible = () => useAppSelector(selectCollectible);

export const useCollectiblePublished = () =>
    useAppSelector(selectCollectiblePublished);
export const useCollectibleCreated = () =>
    useAppSelector(selectCollectibleCreated);
export const useCollectibleError = () => useAppSelector(selectCollectibleError);

export const useSetSelectedLot = () => {
    const dispatch = useAppDispatch();

    return useCallback(
        (lot: LotI) => {
            dispatch(setSelectedLotThunk(lot));
        },
        [dispatch],
    );
};

export const useResetSelectedLot = () => {
    const dispatch = useAppDispatch();

    return useCallback(() => {
        dispatch(CollectibleActions.resetSelectedLot());
    }, [dispatch]);
};

export const useSetSelectedLaunchpad = () => {
    const dispatch = useAppDispatch();

    return useCallback((launchpadId?: string) => {
        dispatch(CollectibleActions.setSelectedLaunchPad(launchpadId));
    }, [dispatch]);
};

export const useResetSelectedLaunchpad = () => {
    const dispatch = useAppDispatch();

    return useCallback(() => {
        dispatch(CollectibleActions.resetSelectedLaunchPad());
    }, [dispatch]);
};

export const useSetSelectedCollection = () => {
    const dispatch = useAppDispatch();

    return useCallback((collectionId?: string) => {
        dispatch(CollectibleActions.setSelectedCollection(collectionId));
    }, [dispatch]);
};

export const useResetSelectedCollection = () => {
    const dispatch = useAppDispatch();

    return useCallback(() => {
        dispatch(CollectibleActions.resetSelectedCollection());
    }, [dispatch]);
};


export const useCurrentBids = () => useAppSelector(selectCurrentBids);
export const useAuctionWinner = () => useAppSelector(selectAuctionWinner);
export const useAllLots = () => useAppSelector(selectAllLots);
export const useSelectedLot = () => useAppSelector(selectSelectedLot);
export const useActiveLots = () => useAppSelector(selectActiveLots);
export const useNftCollection = () => useAppSelector(selectNftCollection);
export const useSelectedLaunchpadId = () => useAppSelector(selectSelectedLaunchpadId);

